import { useState, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Clock, Building, UserCircle, Send, MessageSquare, AlertCircle, Upload, Paperclip, Image, File as FileIcon, Trash2, XCircle, GitMerge, Download, ChevronDown, Calendar, Timer, Check, Pencil, Edit3, HardDrive, Plus } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { API_BASE_URL, ticketsAPI, adminTicketsAPI } from '../services/api';
import { useFeatureFlag } from '../context/featureFlagStore';
import { useAuthStore } from '../context/authStore';
import { useDisplay } from '../hooks/useDisplay';
import AdminTimeEntryModal from '../components/admin/AdminTimeEntryModal';
import AdminScheduleEntryModal from '../components/admin/AdminScheduleEntryModal';
import AdminAddResourceModal from '../components/admin/AdminAddResourceModal';
import AdminChangeOwnerModal from '../components/admin/AdminChangeOwnerModal';
import AdminEditTicketModal from '../components/admin/AdminEditTicketModal';
import AdminLinkConfigurationModal from '../components/admin/AdminLinkConfigurationModal';
import ClientWorkPanel from '../components/tickets/ClientWorkPanel';
import { isInternalRole } from '../utils/roles.js';

// Format date helper
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return 'N/A';
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// Format file size helper
function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// The proxy is public (an <img> cannot send a header), so each ticket's
// detail response hands us a short-lived token naming the tenant and the
// ticket, and the proxy trusts only that. Kept per ticket id so a page
// showing several tickets' notes cannot hand one ticket's token to another.
const imageTokens = new Map();

// Helper to convert ConnectWise inline image URLs to our proxy
function getProxiedImageUrl(src, ticketId) {
  if (!src) return src;

  // Match ConnectWise inline image URLs
  // Format: https://na.myconnectwise.net/v4_6_release/api/inlineimages/{companyId}/{imageId}
  if (src.includes('myconnectwise.net') && src.includes('/inlineimages/')) {
    // Encode the full URL in base64 to pass to our proxy
    const encodedUrl = btoa(src);
    // Include ticketId so backend can try matching to attached documents
    const ticketParam = ticketId ? `&ticketId=${ticketId}` : '';
    const token = ticketId ? imageTokens.get(String(ticketId)) : null;
    const tokenParam = token ? `&t=${encodeURIComponent(token)}` : '';
    return `${API_BASE_URL}/tickets/image-proxy?url=${encodeURIComponent(encodedUrl)}${ticketParam}${tokenParam}`;
  }

  return src;
}

// Image component with fallback for failed loads
function ImageWithFallback({ src, alt, ticketId }) {
  const [failed, setFailed] = useState(false);
  const [loading, setLoading] = useState(true);

  // Use proxied URL for ConnectWise images
  const proxiedSrc = getProxiedImageUrl(src, ticketId);

  if (failed) {
    return (
      <div className="inline-flex items-center gap-2 px-3 py-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg text-sm text-neutral-500 dark:text-neutral-400 my-2">
        <Image className="w-4 h-4" />
        <span>Image could not be loaded</span>
        <a
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:underline ml-1"
        >
          (View original)
        </a>
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div className="inline-block px-3 py-2 bg-neutral-100 dark:bg-neutral-700 rounded-lg text-sm text-neutral-500 dark:text-neutral-400 my-2">
          Loading image...
        </div>
      )}
      <img
        src={proxiedSrc}
        alt={alt}
        className={`max-w-full h-auto rounded-lg my-2 ${loading ? 'hidden' : ''}`}
        onLoad={() => setLoading(false)}
        onError={() => {
          setLoading(false);
          setFailed(true);
        }}
      />
    </>
  );
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isFromAdmin = searchParams.get('from') === 'admin';
  const fromTicket = searchParams.get('fromTicket');
  const [replyText, setReplyText] = useState('');
  const [replyError, setReplyError] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();
  const canCloseTicket = useFeatureFlag('feature.ticket_close');
  const canAttachFiles = useFeatureFlag('feature.ticket_attachments');
  const d = useDisplay();
  const { user, can } = useAuthStore();
  const isInternalStaff = isInternalRole(user?.role);
  const canChangeStatus = isFromAdmin && isInternalStaff;
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const adminMode = isFromAdmin && isInternalStaff;
  const [noteType, setNoteType] = useState('discussion');
  // Co-managed IT: a client's technician may write for their team only.
  const [audience, setAudience] = useState('requester');
  const [showReturn, setShowReturn] = useState(false);
  const [returnNote, setReturnNote] = useState('');
  const [emailContact, setEmailContact] = useState(false);
  const [emailResources, setEmailResources] = useState(false);
  const [ccEmailsRaw, setCcEmailsRaw] = useState('');
  const [replyStatusId, setReplyStatusId] = useState('');
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [showOwnerModal, setShowOwnerModal] = useState(false);
  const [showEditTicketModal, setShowEditTicketModal] = useState(false);
  const [editingScheduleEntry, setEditingScheduleEntry] = useState(null);
  const [editingTimeEntry, setEditingTimeEntry] = useState(null);
  const [showLinkConfigModal, setShowLinkConfigModal] = useState(false);

  // Admin-only: is this ticket's company co-managed, and is the ticket with us?
  const { data: comanagedData, refetch: refetchComanaged } = useQuery({
    queryKey: ['admin-ticket-comanaged', id],
    queryFn: async () => (await adminTicketsAPI.comanaged(id)).data,
    enabled: adminMode,
  });
  const returnToClientMutation = useMutation({
    mutationFn: () => adminTicketsAPI.returnToClient(id, returnNote.trim()),
    onSuccess: () => {
      setShowReturn(false);
      setReturnNote('');
      refetchComanaged();
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
  });

  // Admin-only: time entries on the ticket
  const { data: timeEntriesData, refetch: refetchTimeEntries } = useQuery({
    queryKey: ['admin-ticket-time-entries', id],
    queryFn: async () => (await adminTicketsAPI.listTimeEntries(id)).data,
    enabled: adminMode,
  });
  const timeEntries = timeEntriesData?.items || [];

  const deleteTimeEntryMutation = useMutation({
    mutationFn: (entryId) => adminTicketsAPI.deleteTimeEntry(id, entryId),
    onSuccess: () => {
      refetchTimeEntries();
      queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
    },
  });

  // Admin-only: linked configurations
  const { data: configurationsData, refetch: refetchConfigurations } = useQuery({
    queryKey: ['admin-ticket-configurations', id],
    queryFn: async () => (await adminTicketsAPI.listConfigurations(id)).data,
    enabled: adminMode,
  });
  const linkedConfigurations = configurationsData?.items || [];

  const unlinkConfigurationMutation = useMutation({
    mutationFn: (configId) => adminTicketsAPI.unlinkConfiguration(id, configId),
    onSuccess: () => refetchConfigurations(),
  });

  // Admin-only: notify preview (contact email, resource emails, board notify list)
  const { data: notifyPreviewData } = useQuery({
    queryKey: ['admin-ticket-notify-preview', id],
    queryFn: async () => (await adminTicketsAPI.notifyPreview(id)).data,
    enabled: adminMode,
  });

  // Admin-only: the portal assignee. ConnectWise will not accept an unlicensed
  // person in the Owner field, so staff who work tickets only through the
  // portal are recorded here and mirrored into CW as a note.
  const { data: portalAssigneeData } = useQuery({
    queryKey: ['admin-ticket-portal-assignee', id],
    queryFn: async () => (await adminTicketsAPI.getPortalAssignee(id)).data,
    enabled: adminMode,
  });
  const { data: portalCandidatesData } = useQuery({
    queryKey: ['admin-portal-assignee-candidates'],
    queryFn: async () => (await adminTicketsAPI.portalAssigneeCandidates()).data,
    enabled: adminMode,
  });
  const portalAssignee = portalAssigneeData?.assignee || null;
  const portalCandidates = portalCandidatesData?.users || [];

  const setPortalAssigneeMutation = useMutation({
    mutationFn: (portalUserId) =>
      portalUserId
        ? adminTicketsAPI.setPortalAssignee(id, portalUserId)
        : adminTicketsAPI.clearPortalAssignee(id),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-ticket-portal-assignee', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
      const mirrorError = res?.data?.mirror?.error;
      if (mirrorError) {
        window.alert(`Saved in the portal, but ConnectWise was not updated: ${mirrorError}`);
      }
    },
    onError: (err) => {
      window.alert(err.response?.data?.error || 'Could not change the portal assignee');
    },
  });

  // Admin-only: delete a note. CW may reject if the note is locked by board
  // policy (closed invoice, age window, etc.); surface the message.
  const deleteNoteMutation = useMutation({
    mutationFn: ({ ticketId, noteId }) => adminTicketsAPI.deleteNote(ticketId, noteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
    },
    onError: (err) => {
      window.alert(
        'Could not delete note: ' +
          (err.response?.data?.error || err.response?.data?.details || err.message)
      );
    },
  });

  // Admin-only: schedule entries (calendar blocks AND resource assignments —
  // both come from /schedule/entries; we split them by ownerFlag below)
  const { data: scheduleData, refetch: refetchSchedule } = useQuery({
    queryKey: ['admin-ticket-schedule', id],
    queryFn: async () => {
      const resp = await adminTicketsAPI.listSchedule(id);
      return resp.data;
    },
    enabled: adminMode,
  });
  // Show every schedule entry (assignment + calendar block) in one Resources
  // panel. Calendar info renders inline on the row when the entry has hours.
  const allScheduleEntries = scheduleData?.items || [];

  const deleteScheduleMutation = useMutation({
    mutationFn: (entryId) => adminTicketsAPI.deleteSchedule(id, entryId),
    onSuccess: () => refetchSchedule(),
  });

  const deleteResourceMutation = useMutation({
    mutationFn: (entryId) => adminTicketsAPI.deleteResource(id, entryId),
    onSuccess: () => refetchSchedule(),
  });

  // Mark a schedule entry (or resource) as Done. Mirrors CW's "Done" action —
  // entry stays on the record but is no longer pending work for the member.
  const markDoneMutation = useMutation({
    mutationFn: ({ entryId, done }) => adminTicketsAPI.markScheduleDone(id, entryId, done),
    onSuccess: () => refetchSchedule(),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['ticket', id],
    queryFn: () => ticketsAPI.get(id),
  });
  // Idempotent, so it is fine to do on every render: the token is what lets
  // this ticket's inline images load through the public proxy.
  if (data?.data?.imageToken) imageTokens.set(String(id), data.data.imageToken);

  const { data: notesData, isLoading: notesLoading } = useQuery({
    queryKey: ['ticket', id, 'notes'],
    queryFn: async () => {
      const response = await ticketsAPI.getNotes(id);
      return response.data;
    },
  });

  const { data: documentsData } = useQuery({
    queryKey: ['ticket', id, 'documents'],
    queryFn: async () => {
      const response = await ticketsAPI.getDocuments(id);
      return response.data;
    },
  });

  const addNoteMutation = useMutation({
    mutationFn: async (text) => {
      // Admin mode posts through the admin route so attribution + note-type
      // flags are applied. Customer/internal-staff path keeps the existing
      // behavior (single discussion note via the customer-facing endpoint).
      const ccEmails = ccEmailsRaw
        .split(/[,;\s]+/)
        .map((e) => e.trim())
        .filter((e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e));
      const response = adminMode
        ? await adminTicketsAPI.addNote(id, {
            text,
            noteType,
            emailContact,
            emailResources,
            ccEmails,
            statusId: replyStatusId ? parseInt(replyStatusId) : undefined,
          })
        : await ticketsAPI.addNote(id, text, { audience });

      // Upload attachments if any
      if (attachments.length > 0) {
        setUploadProgress('Uploading attachments...');
        try {
          await ticketsAPI.uploadDocuments(id, attachments);
        } catch (uploadError) {
          console.error('Failed to upload some attachments:', uploadError);
        }
        setUploadProgress(null);
      }

      return response;
    },
    onSuccess: (response) => {
      setReplyText('');
      setAttachments([]);
      setReplyError(null);
      setEmailContact(false);
      setEmailResources(false);
      setCcEmailsRaw('');
      setReplyStatusId('');
      // Surface any partial email failure (note saved, email failed)
      const er = response?.data?.emailResult;
      if (er && er.ok === false) {
        setReplyError(
          'Note saved, but email send failed: ' + (er.errors?.join(', ') || 'unknown error')
        );
      }
      // Refresh notes, documents, and ticket data (in case ticket was reopened
      // or its status was changed alongside the note).
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
      queryClient.invalidateQueries({ queryKey: ['ticket', id, 'documents'] });
    },
    onError: (error) => {
      setUploadProgress(null);
      setReplyError(error.response?.data?.error || 'Failed to add reply');
    }
  });

  const closeTicketMutation = useMutation({
    mutationFn: () => ticketsAPI.close(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Failed to close ticket');
    }
  });

  // Fetch available statuses for the ticket's board (only for internal staff from admin)
  const { data: statusesData } = useQuery({
    queryKey: ['ticket', id, 'statuses'],
    queryFn: async () => {
      const response = await adminTicketsAPI.getStatuses(id);
      return response.data;
    },
    enabled: canChangeStatus,
  });

  // Mutation for updating ticket status
  const updateStatusMutation = useMutation({
    mutationFn: (statusId) => adminTicketsAPI.updateStatus(id, statusId),
    onSuccess: () => {
      setShowStatusDropdown(false);
      queryClient.invalidateQueries({ queryKey: ['ticket', id] });
    },
    onError: (error) => {
      alert(error.response?.data?.error || 'Failed to update status');
    }
  });

  // File handling functions
  const handleFileSelect = useCallback((files) => {
    const newFiles = Array.from(files).filter(file => {
      return !attachments.some(a => a.name === file.name && a.size === file.size);
    });
    setAttachments(prev => [...prev, ...newFiles]);
  }, [attachments]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer?.files;
    if (files?.length > 0) {
      handleFileSelect(files);
    }
  }, [handleFileSelect]);

  const removeAttachment = useCallback((index) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const getFileIcon = (file) => {
    if (file.type.startsWith('image/')) return Image;
    return FileIcon;
  };

  const ticket = data?.data?.ticket;
  const bundle = data?.data?.bundle;
  const notes = notesData?.notes || [];
  const documents = documentsData?.documents || [];

  // Handle document download
  const handleDownload = async (doc) => {
    try {
      const response = await ticketsAPI.downloadDocument(id, doc.id);
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName || 'document';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file');
    }
  };

  // Filter to only show customer-visible notes
  // Show: discussion notes, resolution notes, external notes, and time entries
  // Hide: internal analysis notes
  const visibleNotes = notes.filter(note => {
    // A co-managed team's own note: internal in ConnectWise, but the server
    // only sends it to someone who may work the ticket (teamOnly).
    if (note.teamOnly) return true;

    // Always hide internal analysis notes
    if (note.internalAnalysisFlag) return false;

    // Show time entries (from technicians logging time)
    if (note.isTimeEntry) return true;

    // Show discussion, resolution, or external notes
    if (note.discussionFlag || note.resolutionFlag || note.externalFlag) return true;

    // Show detail description (initial ticket description)
    if (note.detailDescriptionFlag) return true;

    // Hide everything else
    return false;
  });

  if (isLoading) {
    return <div className="animate-fade-in p-8 text-center">Loading ticket...</div>;
  }

  if (!ticket) {
    return <div className="animate-fade-in p-8 text-center">Ticket not found</div>;
  }

  const handleSubmitReply = (e) => {
    e.preventDefault();
    if (!replyText.trim()) return;
    addNoteMutation.mutate(replyText.trim());
  };

  return (
    <div className="animate-fade-in">
      <button
        onClick={() => {
          if (fromTicket) {
            navigate(`/tickets/${fromTicket}${isFromAdmin ? '?from=admin' : ''}`);
          } else if (isFromAdmin) {
            const returnUrl = sessionStorage.getItem('adminTicketsReturnUrl') || '/admin/tickets';
            sessionStorage.removeItem('adminTicketsReturnUrl');
            navigate(returnUrl, { state: { refresh: Date.now() } });
          } else {
            const returnUrl = sessionStorage.getItem('ticketsReturnUrl') || '/tickets';
            sessionStorage.removeItem('ticketsReturnUrl');
            navigate(returnUrl, { state: { refresh: Date.now() } });
          }
        }}
        className="inline-flex items-center gap-2 text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white mb-6"
      >
        <ArrowLeft className="w-4 h-4" /> {fromTicket ? `Back to ticket #${fromTicket}` : 'Back to tickets'}
      </button>

      <div className="card mb-6">
        <div className="card-header">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-xl font-bold text-neutral-900 dark:text-white">{ticket.summary}</h1>
              <p className="text-neutral-500 dark:text-neutral-400 mt-1">Ticket #{ticket.id}</p>
            </div>
            <div className="flex items-center gap-3">
              {/* Status badge - clickable dropdown for internal staff from admin */}
              {canChangeStatus && statusesData?.statuses ? (
                <div className="relative">
                  <button
                    onClick={() => setShowStatusDropdown(!showStatusDropdown)}
                    disabled={updateStatusMutation.isPending}
                    className={`badge ${ticket.closedFlag ? 'badge-success' : 'badge-warning'} cursor-pointer hover:opacity-80 flex items-center gap-1`}
                  >
                    {updateStatusMutation.isPending ? 'Updating...' : ticket.status?.name || 'Open'}
                    <ChevronDown className="w-3 h-3" />
                  </button>
                  {showStatusDropdown && (
                    <>
                      <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowStatusDropdown(false)}
                      />
                      <div className="absolute right-0 top-full mt-1 z-20 bg-white dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg shadow-lg py-1 min-w-[200px] max-h-64 overflow-y-auto">
                        {statusesData.statuses.map((status) => (
                          <button
                            key={status.id}
                            onClick={() => {
                              if (status.id !== ticket.status?.id) {
                                updateStatusMutation.mutate(status.id);
                              } else {
                                setShowStatusDropdown(false);
                              }
                            }}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center justify-between ${
                              status.id === ticket.status?.id
                                ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                                : 'text-neutral-700 dark:text-neutral-300'
                            }`}
                          >
                            <span>{status.name}</span>
                            {status.closedStatus && (
                              <span className="text-xs text-neutral-400">(Closed)</span>
                            )}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <span className={`badge ${ticket.closedFlag ? 'badge-success' : 'badge-warning'}`}>
                  {ticket.status?.name || 'Open'}
                </span>
              )}
              {bundle && (
                <span className="badge bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300 flex items-center gap-1">
                  <GitMerge className="w-3 h-3" />
                  Bundled
                </span>
              )}
              {canCloseTicket && !ticket.closedFlag && !canChangeStatus && (
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to close this ticket?\n\nIf the issue returns, you can reopen this ticket by adding a reply.')) {
                      closeTicketMutation.mutate();
                    }
                  }}
                  disabled={closeTicketMutation.isPending}
                  className="btn-secondary btn-sm"
                >
                  <XCircle className="w-4 h-4" />
                  {closeTicketMutation.isPending ? 'Closing...' : 'Close Ticket'}
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {d.visible('ticketDetail.meta.created') && (
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('ticketDetail.meta.created')}</p>
                <p className="font-medium dark:text-white">{formatDate(ticket._info?.dateEntered || ticket.dateEntered || ticket.enteredDate)}</p>
              </div>
            </div>
            )}
            {d.visible('ticketDetail.meta.priority') && (
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('ticketDetail.meta.priority')}</p>
                <p className="font-medium dark:text-white">{ticket.priority?.name || 'Normal'}</p>
              </div>
            </div>
            )}
            {d.visible('ticketDetail.meta.board') && (
            <div className="flex items-center gap-3">
              <Building className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('ticketDetail.meta.board')}</p>
                <p className="font-medium dark:text-white">{ticket.board?.name || 'N/A'}</p>
              </div>
            </div>
            )}
            {d.visible('ticketDetail.meta.assignedTo') && (
            <div className="flex items-center gap-3">
              <UserCircle className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">{d.label('ticketDetail.meta.assignedTo')}</p>
                <p className="font-medium dark:text-white">
                  {ticket.ownerName || 'Unassigned'}
                </p>
                {/* Shown to everyone who can see the ticket, not only admins:
                    hiding who is working it would defeat the point. */}
                {portalAssignee && (
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    Portal: {portalAssignee.name}
                    {portalAssignee.mirrorError && (
                      <span className="text-amber-600 dark:text-amber-500"> (not written to ConnectWise)</span>
                    )}
                  </p>
                )}
              </div>
            </div>
            )}
          </div>

          {d.visible('ticketDetail.section.description') && (ticket.initialDescription || ticket.description) && (
            <div className="border-t border-neutral-200 dark:border-neutral-700 pt-6">
              <h3 className="font-medium text-neutral-900 dark:text-white mb-2">{d.label('ticketDetail.section.description')}</h3>
              <p className="text-neutral-600 dark:text-neutral-300 whitespace-pre-wrap">{ticket.initialDescription || ticket.description}</p>
            </div>
          )}

        </div>
      </div>

      {/* Bundled Tickets */}
      {bundle && (
        <div className="card mb-6">
          <div className="card-header">
            <h2 className="font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <GitMerge className="w-5 h-5" /> Bundled Tickets
            </h2>
          </div>
          <div className="card-body">
            {/* Parent Ticket Link */}
            {bundle.parentTicket && (
              <div className="mb-4">
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">This ticket is bundled under:</p>
                <Link
                  to={`/tickets/${bundle.parentTicket.id}${isFromAdmin ? '?from=admin' : ''}`}
                  className="flex items-center gap-3 p-3 bg-primary-50 dark:bg-primary-900/30 border border-primary-200 dark:border-primary-800 rounded-lg hover:bg-primary-100 dark:hover:bg-primary-900/50 transition-colors"
                >
                  <div className="flex-1">
                    <span className="font-medium text-primary-700 dark:text-primary-300">
                      #{bundle.parentTicket.id}
                    </span>
                    <span className="mx-2 text-neutral-400">—</span>
                    <span className="text-neutral-700 dark:text-neutral-300">
                      {bundle.parentTicket.summary}
                    </span>
                  </div>
                  <span className="text-xs text-primary-600 dark:text-primary-400 font-medium">
                    View Parent →
                  </span>
                </Link>
              </div>
            )}

            {/* Child Tickets List */}
            {bundle.childTickets && bundle.childTickets.length > 0 && (
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 mb-2">
                  Related tickets bundled with this one ({bundle.childTickets.length}):
                </p>
                <div className="space-y-2">
                  {bundle.childTickets.map((child) => (
                    <Link
                      key={child.id}
                      to={`/tickets/${child.id}?fromTicket=${id}${isFromAdmin ? '&from=admin' : ''}`}
                      className="flex items-center gap-3 p-3 bg-neutral-50 dark:bg-neutral-700/50 border border-neutral-200 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="font-medium text-neutral-700 dark:text-neutral-300">
                          #{child.id}
                        </span>
                        <span className="mx-2 text-neutral-400">—</span>
                        <span className="text-neutral-600 dark:text-neutral-400 truncate">
                          {child.summary}
                        </span>
                      </div>
                      <span className={`badge ${child.closedFlag ? 'badge-success' : 'badge-warning'}`}>
                        {child.status || (child.closedFlag ? 'Closed' : 'Open')}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Co-managed IT: the client technician's controls */}
      {!adminMode && (can('tickets', 'work', ticket.company?.id) || can('tickets', 'escalate', ticket.company?.id) || can('tickets', 'log_time', ticket.company?.id)) && (
        <ClientWorkPanel
          ticketId={id}
          closed={!!ticket.closedFlag}
          onChanged={() => queryClient.invalidateQueries({ queryKey: ['ticket', id] })}
        />
      )}

      {/* Notes/Conversation */}
      {d.visible('ticketDetail.section.conversation') && (
      <div className="card mb-6">
        <div className="card-header">
          <h2 className="font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
            <MessageSquare className="w-5 h-5" /> {d.label('ticketDetail.section.conversation')}
          </h2>
        </div>
        <div className="card-body">
          {notesLoading ? (
            <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">Loading notes...</p>
          ) : visibleNotes.length === 0 ? (
            <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">No notes yet</p>
          ) : (
            <div className="space-y-4">
              {[...visibleNotes].sort((a, b) => {
                const dateA = new Date(a._info?.dateEntered || a.dateCreated || 0);
                const dateB = new Date(b._info?.dateEntered || b.dateCreated || 0);
                return dateB - dateA; // Descending order (newest first)
              }).map((note) => (
                <div key={`${note.ticketId || 'main'}-${note.id}`} className={`border rounded-lg p-4 ${
                  note.resolutionFlag
                    ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/30'
                    : note.bundledFrom
                      ? 'border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-900/20'
                      : 'border-neutral-200 dark:border-neutral-700'
                }`}>
                  {/* Bundled ticket indicator */}
                  {note.bundledFrom && (
                    <div className="flex items-center gap-2 mb-2 pb-2 border-b border-purple-200 dark:border-purple-700">
                      <GitMerge className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-purple-600 dark:text-purple-400">
                        From bundled ticket{' '}
                        <Link
                          to={`/tickets/${note.bundledFrom.ticketId}?fromTicket=${id}${isFromAdmin ? '&from=admin' : ''}`}
                          className="font-medium hover:underline"
                        >
                          #{note.bundledFrom.ticketId}
                        </Link>
                        {note.bundledFrom.summary && (
                          <span className="text-purple-500 dark:text-purple-400"> — {note.bundledFrom.summary}</span>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <UserCircle className={`w-5 h-5 ${note.resolutionFlag ? 'text-green-500 dark:text-green-400' : note.bundledFrom ? 'text-purple-400' : 'text-neutral-400'}`} />
                      <span className="font-medium text-neutral-900 dark:text-white">
                        {note.member?.name || note.contact?.name || 'System'}
                      </span>
                      {note.resolutionFlag && (
                        <span className="badge badge-success">Resolution</span>
                      )}
                      {note.teamOnly && (
                        <span className="badge bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300" title="Visible to your IT team and your support provider, not the requester">
                          Team only
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-neutral-500 dark:text-neutral-400">
                        {formatDate(note._info?.dateEntered || note.dateCreated)}
                      </span>
                      {/* Delete (admin only). Time-entry notes have id strings
                          like "time-NNN" and can't be deleted via the notes
                          endpoint — those should be removed via Time Entries. */}
                      {adminMode &&
                        typeof note.id === 'number' &&
                        !note.bundledFrom && (
                          <button
                            type="button"
                            onClick={() => {
                              if (
                                window.confirm(
                                  'Delete this note? This cannot be undone. ConnectWise may refuse if the note is older than the board\'s edit window.'
                                )
                              ) {
                                deleteNoteMutation.mutate({
                                  ticketId: note.ticketId || id,
                                  noteId: note.id,
                                });
                              }
                            }}
                            className="p-1 text-neutral-400 hover:text-red-500 rounded"
                            title="Delete note"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                    </div>
                  </div>
                  <div className="text-neutral-600 dark:text-neutral-300 prose prose-sm max-w-none prose-a:text-primary-600 prose-a:no-underline hover:prose-a:underline prose-img:max-w-full prose-img:rounded-lg dark:prose-invert">
                    <ReactMarkdown
                      components={{
                        a: ({ href, children }) => (
                          <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                            {children}
                          </a>
                        ),
                        img: ({ src, alt }) => (
                          <ImageWithFallback src={src} alt={alt || ''} ticketId={note.ticketId || id} />
                        ),
                        p: ({ children }) => (
                          <p className="whitespace-pre-wrap mb-2 last:mb-0">{children}</p>
                        ),
                        ol: ({ children }) => (
                          <ol className="list-decimal list-inside my-2 space-y-1">{children}</ol>
                        ),
                        ul: ({ children }) => (
                          <ul className="list-disc list-inside my-2 space-y-1">{children}</ul>
                        ),
                        li: ({ children }) => (
                          <li className="text-neutral-600 dark:text-neutral-300">{children}</li>
                        ),
                        strong: ({ children }) => (
                          <strong className="font-bold text-neutral-900 dark:text-white">{children}</strong>
                        ),
                        em: ({ children }) => (
                          <em className="italic">{children}</em>
                        ),
                      }}
                    >
                      {/* A team note carries "[Acme IT team — Jane Doe]" for ConnectWise readers; here the author is already shown. */}
                      {note.teamOnly ? String(note.text || '').replace(/^\[[^\]\n]*\]\s*/, '') : note.text}
                    </ReactMarkdown>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Reply Form */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold text-neutral-900 dark:text-white">
            {ticket.closedFlag ? 'Reopen Ticket' : 'Add Reply'}
          </h2>
        </div>
          <div className="card-body">
            {replyError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {replyError}
              </div>
            )}
            {uploadProgress && (
              <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-blue-700 text-sm">
                {uploadProgress}
              </div>
            )}
            <form onSubmit={handleSubmitReply}>
              {!adminMode && can('tickets', 'work', ticket.company?.id) && (
                <div className="mb-3 flex flex-wrap items-center gap-3">
                  {[
                    { value: 'requester', label: 'Reply to the requester' },
                    { value: 'team', label: 'Team only' },
                  ].map((o) => (
                    <label key={o.value} className="flex items-center gap-1 text-sm text-neutral-700 dark:text-neutral-200">
                      <input type="radio" name="audience" value={o.value} checked={audience === o.value} onChange={() => setAudience(o.value)} />
                      {o.label}
                    </label>
                  ))}
                  <span className="text-xs text-neutral-500 dark:text-neutral-400">
                    {audience === 'team' ? 'Your IT team and your support provider see this; the requester does not.' : 'The requester sees this and may be emailed.'}
                  </span>
                </div>
              )}
              {adminMode && (
                <div className="mb-3">
                  <div className="flex flex-wrap items-center gap-3 mb-2">
                    {[
                      { value: 'discussion', label: 'Discussion' },
                      { value: 'internal', label: 'Internal Note' },
                      { value: 'resolution', label: 'Resolution' },
                    ].map((o) => (
                      <label key={o.value} className="flex items-center gap-1 text-sm text-neutral-700 dark:text-neutral-200">
                        <input
                          type="radio"
                          name="noteType"
                          value={o.value}
                          checked={noteType === o.value}
                          onChange={() => setNoteType(o.value)}
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">
                    Will be attributed to:{' '}
                    <strong>
                      {(user?.firstName || user?.first_name || 'Portal User')}{' '}
                      {(user?.lastName || user?.last_name || '')}
                    </strong>
                  </div>
                </div>
              )}
              <textarea
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply..."
                rows={4}
                className="input w-full mb-4"
              />

              {/* File Attachments */}
              {canAttachFiles && (
                <div className="mb-4">
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors ${
                      isDragging
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-300 hover:border-neutral-400'
                    }`}
                  >
                    <Upload className={`w-6 h-6 mx-auto mb-2 ${isDragging ? 'text-primary-500' : 'text-neutral-400'}`} />
                    <p className="text-sm text-neutral-600 mb-2">
                      Drag & drop files here
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      onChange={(e) => handleFileSelect(e.target.files)}
                      className="hidden"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      <Paperclip className="w-4 h-4" />
                      Browse files
                    </button>
                  </div>

                  {/* Attachment List */}
                  {attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {attachments.map((file, index) => {
                        const Icon = getFileIcon(file);
                        return (
                          <div
                            key={`${file.name}-${index}`}
                            className="flex items-center gap-3 p-2 bg-neutral-50 rounded-lg"
                          >
                            {file.type.startsWith('image/') ? (
                              <img
                                src={URL.createObjectURL(file)}
                                alt={file.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-neutral-200 rounded flex items-center justify-center">
                                <Icon className="w-5 h-5 text-neutral-500" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-neutral-900 truncate">{file.name}</p>
                              <p className="text-xs text-neutral-500">{formatFileSize(file.size)}</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeAttachment(index)}
                              className="p-1 hover:bg-neutral-200 rounded text-neutral-500 hover:text-red-500"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {ticket.closedFlag && (
                <p className="text-sm text-amber-600 mb-4">
                  This ticket is closed. Submitting a reply will reopen the ticket.
                </p>
              )}

              {adminMode && comanagedData?.comanaged && (
                <div className="mb-3 p-3 border border-blue-200 dark:border-blue-800 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-xs font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wide">Co-managed client</div>
                      <p className="text-xs text-neutral-600 dark:text-neutral-300">
                        {comanagedData.onClientBoard
                          ? "With the client's own IT team."
                          : comanagedData.lastHandoff?.direction === 'to_msp'
                            ? `Escalated by ${comanagedData.lastHandoff.byName || "the client's team"}${comanagedData.lastHandoff.reason ? `: ${comanagedData.lastHandoff.reason}` : ''}`
                            : 'On your board.'}
                      </p>
                    </div>
                    {!comanagedData.onClientBoard && !ticket.closedFlag && (
                      <button type="button" onClick={() => setShowReturn((v) => !v)} className="text-sm text-blue-700 dark:text-blue-300 font-medium whitespace-nowrap">
                        Hand back to their team
                      </button>
                    )}
                  </div>
                  {showReturn && (
                    <div className="mt-3 space-y-2">
                      <textarea value={returnNote} onChange={(e) => setReturnNote(e.target.value)} rows={2} placeholder="What you did, and what they should do next (their team is emailed this)" className="input w-full text-sm" />
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => setShowReturn(false)} className="btn-ghost btn-sm">Cancel</button>
                        <button type="button" onClick={() => returnToClientMutation.mutate()} disabled={returnToClientMutation.isPending} className="btn-primary btn-sm">
                          {returnToClientMutation.isPending ? 'Handing back…' : 'Hand back'}
                        </button>
                      </div>
                      {returnToClientMutation.isError && (
                        <p className="text-xs text-red-600">{returnToClientMutation.error?.response?.data?.error || 'Could not hand the ticket back'}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {adminMode && portalCandidates.length > 0 && (
                <div className="mb-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-700/30">
                  <div className="mb-2">
                    <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
                      Working on it
                    </div>
                    <p className="text-xs text-neutral-500 dark:text-neutral-400">
                      Staff without a ConnectWise licence. ConnectWise refuses them in a ticket&rsquo;s Owner field, so
                      the portal records who holds it here and mirrors that into ConnectWise as a note. Assigning a
                      licensed member clears this, so there is only ever one answer to who is working the ticket.
                    </p>
                  </div>
                  <select
                    value={portalAssignee?.portalUserId || ''}
                    disabled={setPortalAssigneeMutation.isPending}
                    onChange={(e) => setPortalAssigneeMutation.mutate(e.target.value || null)}
                    className="w-full px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800"
                  >
                    <option value="">Nobody</option>
                    {portalCandidates.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                  <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                    Staff who work tickets here but hold no ConnectWise licence, so CW cannot put them in
                    Assigned To. The choice is written to ConnectWise as a note.
                  </p>
                </div>
              )}

              {adminMode && (
                <div className="mb-3 p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-neutral-50 dark:bg-neutral-700/30">
                  <div className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wide mb-2">
                    Email
                  </div>
                  {notifyPreviewData?.cwDefaults && (
                    <div className="mb-2 text-xs text-neutral-500 dark:text-neutral-400">
                      ConnectWise has this ticket set to email contact{' '}
                      <strong>{notifyPreviewData.cwDefaults.contact ? 'on' : 'off'}</strong>, resources{' '}
                      <strong>{notifyPreviewData.cwDefaults.resources ? 'on' : 'off'}</strong>, CC{' '}
                      <strong>{notifyPreviewData.cwDefaults.ccFlag ? 'on' : 'off'}</strong>. The portal sends
                      only what you tick here.
                    </div>
                  )}
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                      <input
                        type="checkbox"
                        checked={emailContact}
                        disabled={!notifyPreviewData?.contact?.email}
                        onChange={(e) => setEmailContact(e.target.checked)}
                      />
                      <span>
                        Email Contact
                        {notifyPreviewData?.contact ? (
                          <span className="text-neutral-500 dark:text-neutral-400">
                            {' '}— {notifyPreviewData.contact.name}
                            {notifyPreviewData.contact.email
                              ? ` <${notifyPreviewData.contact.email}>`
                              : ' (no email on file)'}
                          </span>
                        ) : (
                          <span className="text-neutral-500 dark:text-neutral-400"> — no contact on ticket</span>
                        )}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                      <input
                        type="checkbox"
                        checked={emailResources}
                        disabled={!notifyPreviewData?.resources?.some((r) => r.email)}
                        onChange={(e) => setEmailResources(e.target.checked)}
                      />
                      <span>
                        Email Resources
                        {notifyPreviewData?.resources?.length > 0 ? (
                          <span className="text-neutral-500 dark:text-neutral-400">
                            {' '}— {notifyPreviewData.resources
                              .map((r) => `${r.name}${r.email ? '' : ' (no email)'}`)
                              .join(', ')}
                          </span>
                        ) : (
                          <span className="text-neutral-500 dark:text-neutral-400"> — none assigned</span>
                        )}
                      </span>
                    </label>
                    <div>
                      <label className="block text-sm text-neutral-700 dark:text-neutral-200 mb-1">
                        CC additional addresses
                      </label>
                      <input
                        type="text"
                        value={ccEmailsRaw}
                        onChange={(e) => setCcEmailsRaw(e.target.value)}
                        placeholder="alice@example.com, bob@example.com"
                        className="w-full px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800"
                      />
                      {notifyPreviewData?.cwDefaults?.cc?.length > 0 && (
                        <div className="mt-1 text-xs text-neutral-500 dark:text-neutral-400">
                          Saved on this ticket in ConnectWise:{' '}
                          <span className="font-mono">{notifyPreviewData.cwDefaults.cc.join(', ')}</span>{' '}
                          <button
                            type="button"
                            className="underline hover:no-underline"
                            onClick={() =>
                              setCcEmailsRaw((prev) => {
                                const have = prev
                                  .split(/[,;\s]+/)
                                  .map((v) => v.trim())
                                  .filter(Boolean);
                                const add = notifyPreviewData.cwDefaults.cc.filter((a) => !have.includes(a));
                                return [...have, ...add].join(', ');
                              })
                            }
                          >
                            add them
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  {(emailContact || emailResources || ccEmailsRaw.trim()) && notifyPreviewData?.boardEmailFrom && (
                    <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                      Sent as <code>"{notifyPreviewData.boardEmailFromName}" &lt;{notifyPreviewData.boardEmailFrom}&gt;</code>. Replies route back through ConnectWise and update this ticket.
                    </div>
                  )}
                  {notifyPreviewData?.boardNotifyOnAdd?.length > 0 && (
                    <div className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                      CW notify-on-add (board config, separate from above):{' '}
                      {notifyPreviewData.boardNotifyOnAdd.join(', ')}
                    </div>
                  )}
                  {statusesData?.statuses?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-700">
                      <label className="flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-200">
                        Set status on save:
                        <select
                          value={replyStatusId}
                          onChange={(e) => setReplyStatusId(e.target.value)}
                          className="px-2 py-1 text-sm border border-neutral-300 dark:border-neutral-600 rounded bg-white dark:bg-neutral-800"
                        >
                          <option value="">Don't change</option>
                          {statusesData.statuses.map((status) => (
                            <option key={status.id} value={status.id}>
                              {status.name}
                            </option>
                          ))}
                        </select>
                      </label>
                    </div>
                  )}
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="submit"
                  disabled={!replyText.trim() || addNoteMutation.isPending}
                  className="btn-primary"
                >
                  <Send className="w-4 h-4" />
                  {addNoteMutation.isPending
                    ? (uploadProgress ? 'Uploading...' : 'Sending...')
                    : ticket.closedFlag ? 'Reopen & Send' : 'Send Reply'}
                </button>
                {adminMode && (
                  <>
                    <button
                      type="button"
                      onClick={() => setShowTimeModal(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <Timer className="w-4 h-4" />
                      Log Time
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowScheduleModal(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <Calendar className="w-4 h-4" />
                      Schedule
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowResourceModal(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <UserCircle className="w-4 h-4" />
                      Add Resource
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowOwnerModal(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <UserCircle className="w-4 h-4" />
                      Change Owner
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowEditTicketModal(true)}
                      className="inline-flex items-center gap-1 px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
                    >
                      <Edit3 className="w-4 h-4" />
                      Edit Ticket
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>

        {/* Admin: Resources (everyone assigned, with schedule info inline) */}
        {adminMode && allScheduleEntries.length > 0 && (
          <div className="card mt-6">
            <div className="card-header">
              <h2 className="font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                <UserCircle className="w-5 h-5" />
                Resources
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Assigned to the ticket. Rows with a time also reserve that slot on the resource's calendar.
              </p>
            </div>
            <div className="card-body">
              <div className="space-y-2">
                {allScheduleEntries.map((entry) => {
                  const memberName =
                    entry.member?.name || entry.member?.identifier || 'Unassigned';
                  const isOwner = ticket?.owner?.id && ticket.owner.id === entry.member?.id;
                  const isDone = entry.doneFlag === true;
                  const isScheduled = entry.hours > 0 && entry.dateStart && entry.dateEnd;
                  return (
                    <div
                      key={entry.id}
                      className={`flex items-center justify-between p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg ${isDone ? 'opacity-60' : ''}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className={`font-medium text-neutral-900 dark:text-white ${isDone ? 'line-through' : ''}`}>
                            {memberName}
                          </div>
                          {isOwner && (
                            <span className="text-xs px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded">
                              Owner
                            </span>
                          )}
                          {isScheduled && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Scheduled
                            </span>
                          )}
                          {isDone && (
                            <span className="text-xs px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded">
                              Done
                            </span>
                          )}
                        </div>
                        {isScheduled && (
                          <div className="text-sm text-neutral-500 dark:text-neutral-400 mt-1">
                            {new Date(entry.dateStart).toLocaleString()} –{' '}
                            {new Date(entry.dateEnd).toLocaleTimeString()}
                            {entry.status?.name && ` • ${entry.status.name}`}
                            {entry.where?.name && ` • ${entry.where.name}`}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {isScheduled && (
                          <button
                            type="button"
                            onClick={() => setEditingScheduleEntry(entry)}
                            className="p-1.5 text-neutral-400 hover:text-primary-500"
                            title="Edit schedule"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => markDoneMutation.mutate({ entryId: entry.id, done: !isDone })}
                          className={`p-1.5 ${isDone ? 'text-green-500 hover:text-neutral-500' : 'text-neutral-400 hover:text-green-500'}`}
                          title={isDone ? 'Mark not done' : 'Mark done'}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            const msg = isScheduled
                              ? 'Delete this schedule entry?'
                              : `Remove ${memberName} as a resource?`;
                            if (window.confirm(msg)) {
                              if (isScheduled) {
                                deleteScheduleMutation.mutate(entry.id);
                              } else {
                                deleteResourceMutation.mutate(entry.id);
                              }
                            }
                          }}
                          className="p-1.5 text-neutral-400 hover:text-red-500"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Admin: Time Entries */}
        {adminMode && timeEntries.length > 0 && (
          <div className="card mt-6">
            <div className="card-header">
              <h2 className="font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                <Timer className="w-5 h-5" />
                Time Entries
              </h2>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                Hours logged against this ticket.
              </p>
            </div>
            <div className="card-body">
              <div className="space-y-2">
                {timeEntries.map((te) => (
                  <div
                    key={te.id}
                    className="flex items-center justify-between p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-neutral-900 dark:text-white">
                        {te.hours} hr{te.hours === 1 ? '' : 's'} • {te.member?.name || te.member?.identifier}
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400">
                        {te.timeStart && new Date(te.timeStart).toLocaleString()}
                        {te.workType?.name && ` • ${te.workType.name}`}
                        {te.billableOption && ` • ${te.billableOption}`}
                      </div>
                      {te.notes && (
                        <div className="text-sm text-neutral-600 dark:text-neutral-300 mt-1 italic">
                          {te.notes}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => setEditingTimeEntry(te)}
                        className="p-1.5 text-neutral-400 hover:text-primary-500"
                        title="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Delete this time entry?')) {
                            deleteTimeEntryMutation.mutate(te.id);
                          }
                        }}
                        className="p-1.5 text-neutral-400 hover:text-red-500"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Admin: Configurations (linked assets) */}
        {adminMode && (
          <div className="card mt-6">
            <div className="card-header flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
                  <HardDrive className="w-5 h-5" />
                  Configurations
                </h2>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
                  Assets linked to this ticket.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowLinkConfigModal(true)}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-neutral-300 dark:border-neutral-600 rounded-lg hover:bg-neutral-100 dark:hover:bg-neutral-700"
              >
                <Plus className="w-4 h-4" />
                Link
              </button>
            </div>
            <div className="card-body">
              {linkedConfigurations.length === 0 ? (
                <div className="text-sm text-neutral-500 dark:text-neutral-400">
                  No configurations linked. Click Link to attach an asset.
                </div>
              ) : (
                <div className="space-y-2">
                  {linkedConfigurations.map((cfg) => (
                    <div
                      key={cfg.id}
                      className="flex items-center justify-between p-3 border border-neutral-200 dark:border-neutral-700 rounded-lg"
                    >
                      <div className="min-w-0">
                        <div className="font-medium text-neutral-900 dark:text-white truncate">
                          {cfg.name}
                        </div>
                        <div className="text-xs text-neutral-500 dark:text-neutral-400">
                          {cfg.type?.name}
                          {cfg.status?.name && ` • ${cfg.status.name}`}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm(`Unlink ${cfg.name}?`)) {
                            unlinkConfigurationMutation.mutate(cfg.id);
                          }
                        }}
                        className="p-1.5 text-neutral-400 hover:text-red-500"
                        title="Unlink"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Admin modals */}
        {showTimeModal && (
          <AdminTimeEntryModal
            ticketId={id}
            onClose={() => setShowTimeModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
              refetchTimeEntries();
            }}
          />
        )}
        {editingTimeEntry && (
          <AdminTimeEntryModal
            ticketId={id}
            existing={editingTimeEntry}
            onClose={() => setEditingTimeEntry(null)}
            onSuccess={() => {
              refetchTimeEntries();
              queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
            }}
          />
        )}
        {editingScheduleEntry && (
          <AdminScheduleEntryModal
            ticketId={id}
            existing={editingScheduleEntry}
            onClose={() => setEditingScheduleEntry(null)}
            onSuccess={() => refetchSchedule()}
          />
        )}
        {showEditTicketModal && (
          <AdminEditTicketModal
            ticket={ticket}
            onClose={() => setShowEditTicketModal(false)}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: ['ticket', id] })}
          />
        )}
        {showLinkConfigModal && (
          <AdminLinkConfigurationModal
            ticketId={id}
            companyId={ticket?.company?.id}
            onClose={() => setShowLinkConfigModal(false)}
            onSuccess={() => refetchConfigurations()}
          />
        )}
        {showScheduleModal && (
          <AdminScheduleEntryModal
            ticketId={id}
            defaultMemberId={ticket?.owner?.id}
            onClose={() => setShowScheduleModal(false)}
            onSuccess={() => refetchSchedule()}
          />
        )}
        {showResourceModal && (
          <AdminAddResourceModal
            ticketId={id}
            onClose={() => setShowResourceModal(false)}
            onSuccess={() => {
              refetchSchedule();
              queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
            }}
          />
        )}
        {showOwnerModal && (
          <AdminChangeOwnerModal
            ticketId={id}
            currentOwner={ticket?.owner}
            onClose={() => setShowOwnerModal(false)}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['ticket', id] });
              queryClient.invalidateQueries({ queryKey: ['ticket', id, 'notes'] });
              refetchSchedule();
            }}
          />
        )}

      {/* Attachments */}
      {d.visible('ticketDetail.section.attachments') && documents.length > 0 && (
        <div className="card mt-6">
          <div className="card-header">
            <h2 className="font-semibold text-neutral-900 dark:text-white flex items-center gap-2">
              <Paperclip className="w-5 h-5" />
              {d.label('ticketDetail.section.attachments')} ({documents.length})
            </h2>
          </div>
          <div className="card-body">
            <div className="space-y-2">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-neutral-50 dark:bg-neutral-700/50 rounded-lg border border-neutral-200 dark:border-neutral-600"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {doc.isImage ? (
                      <Image className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                    ) : (
                      <FileIcon className="w-5 h-5 text-neutral-400 flex-shrink-0" />
                    )}
                    <span className="text-sm text-neutral-700 dark:text-neutral-300 truncate">
                      {doc.fileName || doc.title || 'Untitled'}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDownload(doc)}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-md transition-colors flex-shrink-0"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
